import { isEmpty } from 'lodash';
import React, { FC, useMemo } from 'react';
import { canSideRotationIsAvailable } from 'src/components/pages/products/helpers';
import ValidationMessage from 'src/components/widgets/validation/validationMessage';

import Loader from 'src/components/widgets/loader';
import { NotificationBanner } from 'src/components/widgets/notificationBanner/notificationBanner';
import { AiSparkles } from 'src/components/widgets/svg/aiSparkles';
import { T, validateInputSecurity } from 'src/shared/helpers';
import MdDelete from 'src/shared/material_icons/action/delete.svg';
import MdSwapHoriz from 'src/shared/material_icons/action/swap_horiz.svg';
import MdDuplicate from 'src/shared/material_icons/content/content_copy.svg';
import MdUpload from 'src/shared/material_icons/file/upload.svg';
import { useValidation } from 'src/shared/validation';
import styled, { css, keyframes } from 'styled-components';
import { GhostButton, Link, PrimaryButton } from '../../../../widgets/buttons';
import { Checkbox } from '../../../../widgets/checkbox';
import { Icon } from '../../../../widgets/icon';
import { Input } from '../../../../widgets/input';
import { ZTooltip } from '../../../../widgets/ztooltip';
import { editSideNameAction, useProductConfigurationState } from '../../state';
import { ModelColor } from '../../state/model/modelColor';
import { ModelSide } from '../../state/model/modelSide';
import { useModelSideManager } from '../../state/model/modelSideManager';
import PrintAreaPreview from './printAreaPreview';

const ArrowContent = (props: { className?: string }) => (
    <svg className={props.className} version='1.2' preserveAspectRatio='none' viewBox='0 0 52 44'>
        <path
            d='M14.8,31.9c8.2,0,22.1-3.1,28.6-22.6l-1.3-0.4c-3.8,11.4-10.5,18.5-20,20.9C15,31.6,8.9,30,8.8,30l-0.3,1.3  C8.5,31.3,11.1,31.9,14.8,31.9z'
            vectorEffect='non-scaling-stroke'
            fill='rgb(68, 90, 100);'
        ></path>
        <path
            d='M13.6,34.5c0.2,0,0.4-0.1,0.6-0.3c0.2-0.3,0.1-0.7-0.2-0.9l-4.1-2.6l3.3-2c0.3-0.2,0.4-0.6,0.2-0.9  c-0.2-0.3-0.6-0.4-0.9-0.2l-4.2,2.6C8.1,30.3,8,30.5,8,30.8c0,0.2,0.1,0.5,0.3,0.6l5,3.2C13.4,34.4,13.5,34.5,13.6,34.5z'
            vectorEffect='non-scaling-stroke'
            fill=' rgb(68, 90, 100);'
        ></path>
    </svg>
);

const Arrow = styled(ArrowContent)`
    position: absolute;
    left: -40px;
    top: 90px;
    width: 70px;
    fill: ${(props) => props.theme.app.primary};
`;

const SideImageWrapper = styled.div`
    position: relative;
    width: 250px;
    height: 250px;
`;

// Gentle "breathing" pulse for the waiting-for-image slot: the glow and the dashed border softly
// swell and fade. Unlike a shimmer (which slides and reads as "loading"), this pulses in place and
// reads as "drop your image here". The accent color is passed in via the --placeholder-accent custom
// property so the keyframes can stay theme-aware.
const placeholderBreath = keyframes`
    0%,
    100% {
        border-color: #cfd8dc;
        box-shadow:
            0 1px 2px rgba(0, 0, 0, 0.04),
            0 6px 18px rgba(0, 0, 0, 0.06);
    }
    50% {
        border-color: var(--placeholder-accent);
        box-shadow:
            0 2px 6px rgba(0, 0, 0, 0.06),
            0 16px 40px rgba(0, 0, 0, 0.14);
    }
`;

const SideImage = styled.div<{ $placeholder?: boolean }>`
    position: relative;
    border: 1px ${(props) => (props.$placeholder ? '#cfd8dc dashed' : `${props.theme.app.primary_light} solid`)};
    border-radius: 3px;
    background-color: #fff;
    width: 100%;
    height: 100%;
    padding: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: ${(props) => props.theme.text.boldWeight};

    /* Before the image exists (from-scratch), the slot gently "breathes" (glow + border pulse) so it
       reads as an interactive "waiting for your image here" card rather than a loading skeleton. */
    ${(props) =>
        props.$placeholder &&
        css`
            --placeholder-accent: ${props.theme.app.primary};
            box-shadow:
                0 1px 2px rgba(0, 0, 0, 0.04),
                0 10px 28px rgba(0, 0, 0, 0.08);
            animation: ${placeholderBreath} 2.6s ease-in-out infinite;

            @media (prefers-reduced-motion: reduce) {
                animation: none;
            }
        `}

    img {
        width: 100%;
        height: 100%;
        display: block;
        object-fit: contain;
    }
`;

// While the side has no image yet (from-scratch), the not-yet-active fields (name, print area, options)
// are shown as static dashed placeholders. Unlike SkeletonBlock (shimmer), this reads as a calm
// "to be filled" field rather than something that is loading.
const FieldPlaceholder = styled.div<{ $width?: string; $height?: string; $radius?: string; $round?: boolean }>`
    width: ${(props) => props.$width ?? '100%'};
    height: ${(props) => props.$height ?? '1em'};
    border-radius: ${(props) => (props.$round ? '50%' : (props.$radius ?? '4px'))};
    border: 1px dashed ${(props) => props.theme.app.borderColor};
    background-color: #fafbfc;
    flex-shrink: 0;
`;

const PlaceholderContent = styled.div`
    position: relative;
    z-index: 1;
`;

const ImageOverlayIcons = styled.div`
    position: absolute;
    bottom: 8px;
    right: 8px;
    display: flex;
    gap: 4px;
`;

const ImageOverlayButton = styled(GhostButton)`
    background: #fff;
    border: 1px solid #cfd8dc;
    border-radius: 8px;
    padding: 8px;
    color: #37474f;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);

    &:hover {
        background: #f5f5f5;
        box-shadow: 0 3px 10px rgba(0, 0, 0, 0.25);
    }

    ${Icon} {
        width: 22px;
        height: 22px;
    }
`;

// Identica scatola bianca di ImageOverlayButton (upload/swap), ma con la stellina a gradiente.
const AiOverlayButton = styled(ImageOverlayButton)`
    ${Icon} {
        width: 22px;
        height: 22px;
    }
`;

const SideNameField = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: 10px;
`;

const SideCode = styled.span`
    margin-top: 4px;
    font-size: 11px;
    line-height: 1.3;
    color: #78909c;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const AreaContainer = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    grid-gap: 40px;
    position: relative;
    align-items: stretch;
`;

const Icons = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    align-items: center;
    gap: 4px;
`;

const IconButton = styled(GhostButton)`
    color: #455a64;
`;

// The content_copy glyph fills its viewBox almost edge-to-edge, so it renders visually larger than the
// drag/delete icons at the same box size. Shrink it a touch so all three icons look the same size.
const CopyIconButton = styled(IconButton)`
    ${Icon} svg {
        width: 14px;
        height: 14px;
    }
`;

const SidePanel = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 16px;
    min-width: 300px;
    max-width: 340px;
`;

const SectionBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const SectionHeader = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: #90a4ae;
    text-transform: uppercase;
    letter-spacing: 1.2px;
`;

const HelpMessage = styled.div`
    font-size: 14px;
    padding-left: 30px;
    padding-top: 40px;
    position: relative;
    min-width: 250px;
    line-height: 18px;

    span {
        margin-right: 50px;
        margin-bottom: 20px;
    }
`;

const AddSideMessageContent: FC<{ className?: string; label?: string; onUploadClick?: () => void; onAiClick?: () => void }> = ({
    className,
    label,
    onUploadClick,
    onAiClick
}) => {
    return (
        <div className={className}>
            <span>{label ?? T._('Add another side', 'Admin')}</span>
            <AddSideIcons>
                <ZTooltip title={T._('Upload image', 'Admin')}>
                    <ImageOverlayButton
                        type='button'
                        $size='small'
                        $type='icon'
                        aria-label={T._('Upload image', 'Admin')}
                        onClick={(e) => {
                            e.stopPropagation();
                            onUploadClick?.();
                        }}
                    >
                        <Icon>
                            <MdUpload />
                        </Icon>
                    </ImageOverlayButton>
                </ZTooltip>
                <ZTooltip title={T._('Generate image with AI', 'Admin')}>
                    <AiOverlayButton
                        type='button'
                        $size='small'
                        $type='icon'
                        aria-label={T._('Generate image with AI', 'Admin')}
                        onClick={(e) => {
                            e.stopPropagation();
                            onAiClick?.();
                        }}
                    >
                        <Icon>
                            <AiSparkles />
                        </Icon>
                    </AiOverlayButton>
                </ZTooltip>
            </AddSideIcons>
        </div>
    );
};

const AddSideIcons = styled.div`
    display: flex;
    gap: 8px;
    margin-top: 8px;
`;

// Small "optional" pill in the top-right of the "Add another side" card, to signal a second side is not required.
const OptionalLabel = styled.span`
    padding: 1px 6px;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 600;
    text-transform: lowercase;
    background: #eceff1;
    color: #607d8b;
`;

const AddSideMessage = styled(AddSideMessageContent)`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
`;

interface PrintAreaItemProps {
    modelColor: ModelColor | null;
    modelSide: ModelSide | null;
    showRemoveButton: boolean;
    showMandatoryCheckbox: boolean;
    // When false, the side has no real image yet: the image slot shows the centered upload/AI actions
    // (instead of a preview) and the "Set print area" button is disabled. Defaults to true.
    hasImage?: boolean;
    // Forces the image slot loader while an externally-tracked upload (e.g. the product image) is in progress.
    imageUploading?: boolean;
    // Optional label shown above the centered upload/AI actions when hasImage is false.
    imagePlaceholderLabel?: string;
    // Drag handle element (with dnd-kit listeners) that replaces the old up/down sort buttons.
    dragHandle?: React.ReactNode;
    onAddSideUpload?: () => void;
    onAddSideAi?: () => void;
    onAreaChangeClick?: () => void;
    onImageChangeClick?: () => void;
    onGenerateImageWithAi?: () => void;
    onNameChangeClick?: () => void;
    onRemoveClick?: () => void;
    onDuplicateClick?: () => void;
    onSetMandatorySide?: (value: boolean) => void;
    onSetRotateSide?: (value: boolean) => void;
}

const PrintAreaItem: FC<PrintAreaItemProps> = ({
    modelColor,
    modelSide,
    showRemoveButton,
    showMandatoryCheckbox,
    hasImage = true,
    imageUploading = false,
    imagePlaceholderLabel,
    dragHandle,
    onAddSideUpload,
    onAddSideAi,
    onAreaChangeClick,
    onImageChangeClick,
    onGenerateImageWithAi,
    onNameChangeClick,
    onRemoveClick,
    onDuplicateClick,
    onSetMandatorySide,
    onSetRotateSide
}) => {
    const { getUploadStatus } = useModelSideManager();
    const { state, dispatch } = useProductConfigurationState();
    const uploadStatus = modelSide ? getUploadStatus(modelSide) : { uploading: false, progress: 0 };
    const side = useMemo(() => (modelSide ? state.sides.find((x) => x.guid == modelSide.originalSideGuid) : null), [modelSide?.originalSideGuid]);

    const [canUseRotationOrMandatory, textDisable] = canSideRotationIsAvailable(state.product, modelSide, modelColor);

    const handleChangeArea = () => {
        if (modelSide && modelColor && onAreaChangeClick) {
            onAreaChangeClick();
        }
    };

    const handleChangeImage = () => {
        if (modelSide && onImageChangeClick) {
            onImageChangeClick();
        }
    };

    const sideNameSecurityValidation = validateInputSecurity(side?.name ?? '', { strictMode: true });

    const validationSideName = useValidation([
        {
            key: 'validationSideName',
            validate: (value: string) => (isEmpty(value) || !sideNameSecurityValidation.isValid ? true : false),
            message: sideNameSecurityValidation.isValid
                ? T._('Required', 'Admin')
                : `${T._('The side name contains invalid characters', 'Admin')}: ${sideNameSecurityValidation.invalidCharacters.join(', ')}`
        }
    ]);

    // Only the real side cards have a name field to validate. The empty "add another side" card
    // (modelSide == null) must not register the shared name validation, otherwise it flags "Required"
    // and would wrongly block proceeding.
    if (modelSide) validationSideName.check(side?.name ?? '');

    return (
        <AreaContainer>
            <Icons>
                {dragHandle}

                {/* "optional" pill in the top-right of the empty "add another side" card, since a second side is not required. */}
                {!modelSide && <OptionalLabel>{T._('optional', 'Admin')}</OptionalLabel>}

                {showRemoveButton && (
                    <ZTooltip title={T._('Remove this side', 'Admin')}>
                        <IconButton type='button' $size='small' $type='icon' aria-label={T._('Remove this side', 'Admin')} onClick={onRemoveClick}>
                            <Icon>
                                <MdDelete />
                            </Icon>
                        </IconButton>
                    </ZTooltip>
                )}

                {/* Copy stays as the last icon; shown as a skeleton until the product has an image. */}
                {modelSide &&
                    (hasImage ? (
                        <ZTooltip title={T._('Duplicate side', 'Admin')}>
                            <CopyIconButton type='button' $size='small' $type='icon' aria-label={T._('Duplicate side', 'Admin')} onClick={onDuplicateClick}>
                                <Icon>
                                    <MdDuplicate />
                                </Icon>
                            </CopyIconButton>
                        </ZTooltip>
                    ) : (
                        <FieldPlaceholder $round $width='24px' $height='24px' />
                    ))}
            </Icons>

            <div>
                <SideImageWrapper>
                    <SideImage $placeholder={!!modelSide && !imageUploading && !uploadStatus?.uploading && !hasImage}>
                        {modelSide && (imageUploading || uploadStatus?.uploading) && <Loader />}
                        {modelSide && !imageUploading && !uploadStatus?.uploading && hasImage && (
                            <PrintAreaPreview modelSide={modelSide} product={state.product} />
                        )}
                        {modelSide && !imageUploading && !uploadStatus?.uploading && !hasImage && (
                            <PlaceholderContent>
                                <AddSideMessage label={imagePlaceholderLabel} onUploadClick={onImageChangeClick} onAiClick={onGenerateImageWithAi} />
                            </PlaceholderContent>
                        )}
                        {!modelSide && <AddSideMessage onUploadClick={onAddSideUpload} onAiClick={onAddSideAi} />}
                    </SideImage>
                    {modelSide && hasImage && !imageUploading && !uploadStatus?.uploading && (
                        <ImageOverlayIcons>
                            <ZTooltip title={T._('Change image', 'Admin')}>
                                <ImageOverlayButton
                                    type='button'
                                    $size='small'
                                    $type='icon'
                                    aria-label={T._('Change image', 'Admin')}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleChangeImage();
                                    }}
                                >
                                    <Icon>
                                        <MdSwapHoriz />
                                    </Icon>
                                </ImageOverlayButton>
                            </ZTooltip>
                            <ZTooltip title={T._('Generate image with AI', 'Admin')}>
                                <AiOverlayButton
                                    type='button'
                                    $size='small'
                                    $type='icon'
                                    aria-label={T._('Generate image with AI', 'Admin')}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onGenerateImageWithAi?.();
                                    }}
                                >
                                    <Icon>
                                        <AiSparkles />
                                    </Icon>
                                </AiOverlayButton>
                            </ZTooltip>
                        </ImageOverlayIcons>
                    )}
                </SideImageWrapper>
                {side && (
                    <SideNameField>
                        {hasImage ? (
                            <>
                                <Input
                                    fullWidth
                                    type='text'
                                    value={side.name}
                                    onChange={(e) => {
                                        if (!modelSide) return;
                                        dispatch(editSideNameAction(modelSide, e.target.value));
                                    }}
                                    placeholder={T._('Enter side name', 'Admin')}
                                />
                                {modelSide?.code && (
                                    <SideCode>
                                        {T._('Code', 'Admin')}: {modelSide.code}
                                    </SideCode>
                                )}
                                <ValidationMessage validationKey='validationSideName' />
                            </>
                        ) : (
                            <FieldPlaceholder $height='32px' $radius='4px' />
                        )}
                    </SideNameField>
                )}
            </div>

            {!modelSide && (
                <HelpMessage>
                    <Arrow />
                    <span>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: T._('If your product<br />has multiple sides<br />that can be customized,<br />add them here.', 'Admin')
                            }}
                        />
                        <br />
                        <br />
                        <Link target='_blank' href=' https://zakeke.zendesk.com/hc/en-us/articles/360009494599-Side'>
                            {T._('What is a side?', 'Admin')}
                        </Link>
                    </span>
                </HelpMessage>
            )}

            {modelSide && (
                <SidePanel>
                    {/* PRINT AREA — shown as a static placeholder until the product has an image. */}
                    <SectionBlock>
                        <SectionHeader>{T._('Print Area', 'Admin')}</SectionHeader>
                        {hasImage ? (
                            <>
                                <PrimaryButton $size='medium' onClick={handleChangeArea}>
                                    {modelSide.areas.length === 0 ? T._('Set print area', 'Admin') : T._('Edit print area', 'Admin')}
                                </PrimaryButton>
                                {/* Partial products (e.g. bulk-imported without PPCM) can have a print area but no
                                    measurement. Warn the user to edit the print area to add it. */}
                                {modelSide.areas.length > 0 && modelSide.ppcm <= 0 && (
                                    <NotificationBanner
                                        type='WARNING'
                                        variant='slim'
                                        content={T._('This print area has no measurement (PPCM). Edit the print area to add it.', 'Admin')}
                                    />
                                )}
                            </>
                        ) : (
                            <FieldPlaceholder $height='40px' $radius='4px' />
                        )}
                    </SectionBlock>

                    {/* OPTIONS — shown as a static placeholder until the product has an image. */}
                    <SectionBlock>
                        <SectionHeader>{T._('Options', 'Admin')}</SectionHeader>
                        {hasImage ? (
                            <>
                                {showMandatoryCheckbox && (
                                    <Checkbox
                                        label={T._('Make customization mandatory', 'Admin')}
                                        checked={modelSide.mandatory || false}
                                        disabled={!canUseRotationOrMandatory}
                                        disabledTooltip={textDisable}
                                        onChange={(e) => {
                                            onSetMandatorySide!(e);
                                        }}
                                        caption={T._('Customers must customize this side before purchasing.', 'Admin')}
                                    />
                                )}
                                <Checkbox
                                    label={T._('Allow product rotation', 'Admin')}
                                    checked={modelSide.canRotate}
                                    disabled={!canUseRotationOrMandatory}
                                    disabledTooltip={textDisable}
                                    onChange={(e) => {
                                        onSetRotateSide!(e);
                                    }}
                                    caption={
                                        <>
                                            {T._('Let customers rotate the product in the customizer.', 'Admin')}{' '}
                                            <Link target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/11739151453468-Side-rotation-in-UI'>
                                                {T._('Learn more', 'Admin')}
                                            </Link>
                                        </>
                                    }
                                />
                            </>
                        ) : (
                            <>
                                <FieldPlaceholder $height='18px' $width='85%' />
                                <FieldPlaceholder $height='18px' $width='65%' />
                            </>
                        )}
                    </SectionBlock>
                </SidePanel>
            )}
        </AreaContainer>
    );
};

export default PrintAreaItem;
